import {UseToastOptions} from '@chakra-ui/react'
import {useEffect, useRef, useState} from 'react'

import {useActivitySync} from 'lib/activity'
import useIsOnline from 'lib/hooks/use-is-online'
import useToast from 'lib/hooks/use-toast'
import useUser from 'lib/hooks/use-user'

const API_IS_VALIDATING_TIMEOUT = 5_000
const API_IS_UNUSABLE_TEXT = 'Lost connection to Conveyal servers'

const defaultOptions: UseToastOptions = {
  duration: null,
  status: 'error',
  title: null
}

/**
 * Show a toast if there are issues connecting to the backend API.
 */
export default function useApiStatusToast() {
  const [showIsValidating, setShowIsValidating] = useState(false)
  const isOnline = useIsOnline()
  const activityResponse = useActivitySync()
  const toast = useToast()
  const {user, isLoading: userIsLoading} = useUser()
  const {error, isValidating} = activityResponse
  const toastIdRef = useRef<string | number | undefined>(undefined)
  const [toastOptions, setToastOptions] = useState<null | UseToastOptions>(null)

  // Wait to show the `isValidating` timeout toast.
  useEffect(() => {
    if (!isValidating) {
      setShowIsValidating(false)
    } else {
      const id = setTimeout(
        () => setShowIsValidating(true),
        API_IS_VALIDATING_TIMEOUT
      )
      return () => {
        clearTimeout(id)
        setShowIsValidating(false)
      }
    }
  }, [isValidating])

  // Detect if an error toast should be shown.
  useEffect(() => {
    if (isOnline) {
      if (!userIsLoading && !user) {
        return setToastOptions({
          ...defaultOptions,
          title: 'You must be logged in to use Conveyal'
        })
      } else if (error) {
        if (error.status === 401 || error.status === 403) {
          return setToastOptions({
            ...defaultOptions,
            title:
              'Unauthorized or expired access. Redirecting user to login again...'
          })
        } else {
          return setToastOptions({
            ...defaultOptions,
            title: API_IS_UNUSABLE_TEXT,
            description: 'Contact your support team if this message persists.'
          })
        }
      } else if (
        isValidating &&
        showIsValidating &&
        document.visibilityState !== 'hidden'
      ) {
        return setToastOptions({
          ...defaultOptions,
          status: 'info',
          title: 'Establishing connection, please wait...'
        })
      }
    } else if (error || showIsValidating) {
      return setToastOptions({
        ...defaultOptions,
        title: API_IS_UNUSABLE_TEXT,
        description: 'Check your internet connection.'
      })
    }

    setToastOptions(null)
  }, [error, isOnline, isValidating, showIsValidating, user, userIsLoading])

  // Update the toast
  useEffect(() => {
    if (toastIdRef.current != null) {
      if (toastOptions !== null) {
        toast.update(toastIdRef.current, toastOptions)
      } else {
        toast.close(toastIdRef.current)
        toastIdRef.current = undefined
      }
    } else if (toastOptions) {
      toastIdRef.current = toast(toastOptions)
    }
  }, [toastOptions, toast])
}
